import { useEffect, useState } from "react";
import { Button, Input, Space, message } from "antd";
import { Link } from "react-router";
import { Dialog as ZhDialog } from "zhihao-ui-react";
import { tradeInvoiceService } from "@/api/services/tradeInvoiceService";
import { fileService } from "@/api/services/fileService";
import { useUserStore } from "@/stores/react/authStore";
import { showRequestError } from "@/utils/requestErrorMessage";
import { S_INVOICE_STATE } from "@zhihaoscm/meta";
import {
    ProductDetailTabSection,
    ProductDetailTabTable,
    ProductDetailTabMetrics,
    emptyProductDetailColumns,
    withProductDetailDateRange,
    type ProductDetailFilterValues,
    type ProductDetailTabProps
} from "@/views/pages/productManagement/ProductDetailTabPrimitives";

export type indexProps = ProductDetailTabProps;

/** components / invoiceInfo / index：保留数据展示、编辑提交和父级事件回调。 */
export default function MigrationComponent(props: indexProps) {
    const productId = String(props.data?.productConsignment?.id || props.data?.id || "");
    const [summary, setSummary] = useState({ totalAmount: 0, totalCount: 0 });
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState<ProductDetailFilterValues>({});
    const [reloadToken, setReloadToken] = useState(0);
    const [invalidatingInvoice, setInvalidatingInvoice] = useState<any | null>(null);
    const [invalidReason, setInvalidReason] = useState("");
    const [invalidSubmitting, setInvalidSubmitting] = useState(false);
    const permissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const canRead = permissions.includes("ROLE_INVOICE_R");
    const canDeal = permissions.includes("ROLE_INVOICE_DEAL");

    /** 发票预览优先展示作废凭证，与旧 Vue `invalidFileId || fileId` 一致。 */
    const previewInvoice = async (row: any) => {
        const fileId = row.invalidFileId || row.fileId;
        if (!fileId) {
            message.warning("未上传发票凭证");
            return;
        }
        try {
            const file = await fileService.getFileById(Number(fileId));
            if (file.path) window.open(file.path, "_blank");
        } catch (error) {
            showRequestError(error, "发票凭证预览失败");
        }
    };

    /** 提交作废原因后刷新统计卡和当前发票列表。 */
    const submitInvalidInvoice = async () => {
        if (!invalidatingInvoice?.id || !invalidReason.trim()) {
            message.error("请输入作废原因");
            return;
        }
        setInvalidSubmitting(true);
        try {
            await tradeInvoiceService.invalid(String(invalidatingInvoice.id), { reasons: invalidReason.trim() });
            message.success("作废成功");
            setInvalidatingInvoice(null);
            setInvalidReason("");
            setReloadToken(value => value + 1);
        } catch (error) {
            showRequestError(error, "发票作废失败");
        } finally {
            setInvalidSubmitting(false);
        }
    };
    useEffect(() => {
        let disposed = false;
        if (!productId) return;
        // 发票页签沿用旧 Vue 的 totalCount 与 paging 请求参数。
        void Promise.all([
            tradeInvoiceService.totalCount({ productId, productType: 1 }),
            tradeInvoiceService.paging({
                page: 1,
                size: 10,
                productId,
                productType: 1,
                ...withProductDetailDateRange(filters, "date")
            } as never)
        ])
            .then(([count, page]) => {
                if (disposed) return;
                setSummary(count || { totalAmount: 0, totalCount: 0 });
                setTotal(Number(page?.total || page?.list?.length || 0));
                setRows(
                    (page?.list || []).map((item: any, index: number) => ({
                        id: item.id || item.invoiceId || `invoice-${index}`,
                        开票编号: item.id ? (
                            <Link to={`/tradeManage/invoiceDetail?id=${item.id}&time=${Date.now()}`}>
                                {item.invoiceId || item.id}
                            </Link>
                        ) : (
                            "--"
                        ),
                        订单编号: item.orderId ? (
                            <Link to={`/tradeManage/tradeOrder/detail?id=${item.orderId}&time=${Date.now()}`}>
                                {item.orderId}
                            </Link>
                        ) : (
                            "--"
                        ),
                        采购用户:
                            item.purchaserEnterprise?.name ||
                            item.purchaserEnterprise?.realName ||
                            item.purchaserInfo ||
                            "--",
                        "开票金额(元)": item.amount ?? "--",
                        创建时间: item.createdTime || item.createTime || "--",
                        开票日期: item.invoiceDate || "--",
                        状态: item.stateName || item.state || "--",
                        操作: (
                            <Space size={0}>
                                {canRead ? (
                                    <Button
                                        aria-label={`预览发票-${item.id}`}
                                        disabled={banButton}
                                        size="small"
                                        type="link"
                                        onClick={() => void previewInvoice(item)}
                                    >
                                        预览
                                    </Button>
                                ) : null}
                                {canDeal && Number(item.state) === Number(S_INVOICE_STATE.INVOICED.code) ? (
                                    <Button
                                        aria-label={`作废发票-${item.id}`}
                                        disabled={banButton}
                                        size="small"
                                        type="link"
                                        onClick={() => {
                                            setInvalidatingInvoice(item);
                                            setInvalidReason("");
                                        }}
                                    >
                                        作废
                                    </Button>
                                ) : null}
                            </Space>
                        )
                    }))
                );
            })
            .catch(() => {
                if (!disposed) setRows([]);
            });
        return () => {
            disposed = true;
        };
    }, [productId, filters, reloadToken, canRead, canDeal, banButton]);
    return (
        <ProductDetailTabSection title="发票明细" className="invoice-info" showTitle={false}>
            <ProductDetailTabMetrics
                items={[
                    {
                        label: "已开票总金额（元）",
                        value: Number(summary.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }),
                        type: "money"
                    },
                    { label: "已开票（笔）", value: summary.totalCount, type: "bi" }
                ]}
            />
            <ProductDetailTabTable
                ariaLabel="发票明细"
                columns={emptyProductDetailColumns([
                    "开票编号",
                    "订单编号",
                    "采购用户",
                    "开票金额(元)",
                    "创建时间",
                    "开票日期",
                    "状态",
                    "操作"
                ])}
                data={rows}
                total={total}
                onSearch={setFilters}
            />
            <ZhDialog
                open={invalidatingInvoice !== null}
                title="作废发票"
                width="520px"
                onCancel={() => {
                    if (!invalidSubmitting) setInvalidatingInvoice(null);
                }}
                footer={
                    <Space>
                        <Button disabled={invalidSubmitting} onClick={() => setInvalidatingInvoice(null)}>
                            取消
                        </Button>
                        <Button loading={invalidSubmitting} type="primary" onClick={() => void submitInvalidInvoice()}>
                            确认
                        </Button>
                    </Space>
                }
            >
                <Input.TextArea
                    aria-label="作废原因"
                    maxLength={200}
                    placeholder="请输入作废原因"
                    rows={4}
                    showCount
                    value={invalidReason}
                    onChange={event => setInvalidReason(event.target.value)}
                />
            </ZhDialog>
            {props.children}
        </ProductDetailTabSection>
    );
}
